const normalizeLoose = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value.toLowerCase().trim();
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).toLowerCase().trim();
  }
  return '';
};

const normalizeSku = (value) => normalizeLoose(value).replace(/[^a-z0-9]/g, '');

const flattenValues = (input) => {
  if (input === undefined || input === null) return [];
  const stack = [input];
  const out = [];
  while (stack.length) {
    const current = stack.pop();
    if (current === undefined || current === null) continue;
    if (Array.isArray(current) || current instanceof Set) {
      for (const item of current) stack.push(item);
    } else {
      out.push(current);
    }
  }
  return out;
};

const ORDER_BADGE_EXCLUDED_KEYWORDS = Object.freeze([
  'coaching day',
  '1 anno csen',
  '1annocsen',
]);
const ORDER_BADGE_EXCLUDED_KEYWORDS_NORMALIZED = ORDER_BADGE_EXCLUDED_KEYWORDS.map(normalizeLoose);

const normalizeMatcherConfig = (config) => {
  const keywords = new Set();
  const skus = new Set();

  const pushKeyword = (value) => {
    const normalized = normalizeLoose(value);
    if (normalized) keywords.add(normalized);
  };

  const pushSku = (value) => {
    const normalized = normalizeSku(value);
    if (normalized) skus.add(normalized);
  };

  if (
    typeof config === 'string' ||
    typeof config === 'number' ||
    typeof config === 'boolean' ||
    Array.isArray(config) ||
    config instanceof Set
  ) {
    for (const value of flattenValues(config)) pushKeyword(value);
    return { keywords, skus };
  }

  if (config && typeof config === 'object') {
    for (const value of flattenValues(config.keywords)) pushKeyword(value);
    for (const value of flattenValues(config.skus)) pushSku(value);
  }

  return { keywords, skus };
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    if (date instanceof Date && !Number.isNaN(date.getTime())) return date;
  }
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const str = String(value).trim();
  if (!str) return null;
  const normalized = str.includes('T') ? str : str.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addMonths = (date, months) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const amount = Number(months);
  if (!Number.isFinite(amount) || amount === 0) return new Date(date.getTime());
  const result = new Date(date.getTime());
  result.setMonth(result.getMonth() + amount);
  return result;
};

const collectSkuCandidates = (sku) => {
  const out = new Set();
  const raw = normalizeLoose(sku);
  if (raw) out.add(raw);
  const normalized = normalizeSku(sku);
  if (normalized) out.add(normalized);
  return [...out];
};

const getProductStrings = (product) => {
  if (!product) return [];
  const { sku, name, title, productName } = product;
  const values = [sku, name, title, productName];
  return values.filter(Boolean).map((val) => String(val));
};

const collectOrderStrings = (order) => {
  if (!order || typeof order !== 'object') return [];
  const baseFields = [
    order.title,
    order.name,
    order.productName,
    order.orderName,
    order.orderTitle,
    order.description,
    order.note,
    order.notes,
  ];
  const productStrings = Array.isArray(order?.products)
    ? order.products.flatMap((product) => getProductStrings(product))
    : [];
  return [...baseFields, ...productStrings];
};

export const shouldSkipOrderForBadge = (order) => {
  const values = collectOrderStrings(order)
    .map(normalizeLoose)
    .filter(Boolean);
  if (!values.length) return false;
  return values.some((value) =>
    ORDER_BADGE_EXCLUDED_KEYWORDS_NORMALIZED.some((keyword) => value.includes(keyword))
  );
};

const extractOrderDate = (order) => {
  if (!order || typeof order !== 'object') return null;
  const candidates = [
    order.orderDate,
    order.importedAt,
    order.updatedAt,
    order.createdAt,
    order.date,
    order.timestamp,
    order.completedAt,
  ];
  for (const candidate of candidates) {
    const parsed = toDate(candidate);
    if (parsed) return parsed;
  }
  return null;
};

const lookupProductMeta = (skuCandidates, catalog) => {
  if (!catalog) return null;
  const candidates = flattenValues(skuCandidates)
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  const tryFetch = (key) => {
    if (!key) return null;
    if (catalog instanceof Map) {
      if (catalog.has(key)) return catalog.get(key);
      const lower = key.toLowerCase();
      if (catalog.has(lower)) return catalog.get(lower);
      const upper = key.toUpperCase();
      if (catalog.has(upper)) return catalog.get(upper);
      return null;
    }
    if (typeof catalog === 'object') {
      if (key in catalog) return catalog[key];
      const lower = key.toLowerCase();
      if (lower in catalog) return catalog[lower];
      const upper = key.toUpperCase();
      if (upper in catalog) return catalog[upper];
    }
    return null;
  };

  for (const candidate of candidates) {
    const meta = tryFetch(candidate);
    if (meta) return meta;
    const normalized = normalizeSku(candidate);
    if (normalized && normalized !== candidate) {
      const metaNormalized = tryFetch(normalized);
      if (metaNormalized) return metaNormalized;
    }
  }

  return null;
};

export const ORDER_BADGE_KEYWORDS = Object.freeze({
  green: Object.freeze({
    skus: Object.freeze([
      'plus',
      'fc66',
      'fc6plus6',
      'fc10top',
      '10topsoloallenamento',
      'fc4top',
      'fc6top',
      'fc64',
      'fc6plus4',
      'fc44',
      'fc4plus4',
      'Solo Allenamento 6 +6',
      'FC 6 +3+3',
      // Aggiungi qui altri SKU che devono mostrare il badge verde
    ]),
    keywords: Object.freeze([
      'plus',
      'fc 6 +6',
      'fc 4 top',
      'fc 6 top',
      'fc 10 top',
      '10 top solo allenamento',
      'fc 6 +4',
      'fc 4 +4',
      'FC 1 +1',
      // Aggiungi qui altri nomi di prodotti che devono mostrare il badge verde
    ]),
  }),
  orange: Object.freeze({
    skus: Object.freeze([
      'starter',
      'basic',
      'fc1',
      'fc2',
      'fc3',
      'fc4',
      'fc5',
      'fc6',
      // Aggiungi qui altri SKU che devono mostrare il badge arancione
    ]),
    keywords: Object.freeze([
      'starter',
      'basic',
      'fc 1',
      'fc 4 programmi',
      'fc 6 programmi',
      // Aggiungi qui altri nomi di prodotti che devono mostrare il badge arancione
    ]),
  }),
});

export const ORDER_BADGE_SKU_MAP = Object.freeze(
  Object.entries(ORDER_BADGE_KEYWORDS).reduce((acc, [color, config]) => {
    const { skus } = normalizeMatcherConfig(config);
    for (const sku of skus) {
      if (!acc[sku]) acc[sku] = color;
    }
    return acc;
  }, {})
);

export const keywordMatches = (values = [], config = []) => {
  const flattened = flattenValues(values).map(normalizeLoose).filter(Boolean);
  if (!flattened.length) return false;

  const { keywords, skus } = normalizeMatcherConfig(config);
  if (!keywords.size && !skus.size) return false;

  if (skus.size) {
    const normalizedSkus = new Set(
      flattenValues(values)
        .map(normalizeSku)
        .filter(Boolean)
    );
    for (const sku of normalizedSkus) {
      if (skus.has(sku)) {
        return true;
      }
    }
  }

  if (keywords.size) {
    for (const candidate of flattened) {
      for (const keyword of keywords) {
        if (candidate.includes(keyword)) return true;
      }
    }
  }

  return false;
};

export const resolveBadgeFromProducts = (products = []) => {
  const entries = flattenValues(products).filter(Boolean);
  if (!entries.length) return 'neutral';

  const identifiers = entries.flatMap((item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') {
      const { sku, name, title, productName } = item;
      return [sku, name, title, productName];
    }
    return [];
  });

  const normalizedSkus = new Set(identifiers.map(normalizeSku).filter(Boolean));
  for (const sku of normalizedSkus) {
    const color = ORDER_BADGE_SKU_MAP[sku];
    if (color) return color;
  }

  if (keywordMatches(identifiers, ORDER_BADGE_KEYWORDS.green)) return 'green';
  if (keywordMatches(identifiers, ORDER_BADGE_KEYWORDS.orange)) return 'orange';
  return 'neutral';
};

export const resolveBadgeFromOrders = (orders = [], options = {}) => {
  const flattenedOrders = flattenValues(orders).filter((order) => order && typeof order === 'object');
  if (!flattenedOrders.length) return 'neutral';

  const nowOption = options.now ? toDate(options.now) : null;
  const now = nowOption || new Date();
  const catalog = options.productsCatalog || options.catalog || null;
  const defaultDurationMonths = Number(options.defaultDurationMonths || 0) || 0;
  const colorsPriority = Array.isArray(options.priority) && options.priority.length
    ? options.priority.map((color) => String(color)).filter((color) => ORDER_BADGE_KEYWORDS[color])
    : Object.keys(ORDER_BADGE_KEYWORDS);

  const activeMatches = new Map();

  for (const order of flattenedOrders) {
    if (shouldSkipOrderForBadge(order)) continue;
    const orderDate = extractOrderDate(order);
    if (!orderDate) continue;
    const products = Array.isArray(order.products) ? order.products : [];
    if (!products.length) continue;

    for (const product of products) {
      const identifiers = getProductStrings(product);
      if (!identifiers.length) continue;

      const skuCandidates = collectSkuCandidates(product?.sku);
      let matchedColor = null;

      for (const skuCandidate of skuCandidates) {
        const normalized = normalizeSku(skuCandidate);
        if (normalized && ORDER_BADGE_SKU_MAP[normalized]) {
          matchedColor = ORDER_BADGE_SKU_MAP[normalized];
          break;
        }
      }

      if (!matchedColor) {
        for (const color of colorsPriority) {
          if (keywordMatches(identifiers, ORDER_BADGE_KEYWORDS[color])) {
            matchedColor = color;
            break;
          }
        }
      }

      if (!matchedColor) continue;

      let durationMonths = defaultDurationMonths;
      const productMeta = lookupProductMeta([skuCandidates, product?.sku], catalog);
      if (productMeta && typeof productMeta === 'object') {
        const rawDuration =
          productMeta.duration_months ??
          productMeta.durationMonths ??
          productMeta.duration ??
          productMeta.months ??
          productMeta.validForMonths ??
          productMeta.validityMonths;
        const parsedDuration = Number(rawDuration);
        if (Number.isFinite(parsedDuration) && parsedDuration > 0) {
          durationMonths = parsedDuration;
        }
      }

      const expiry = durationMonths > 0 ? addMonths(orderDate, durationMonths) : new Date(orderDate.getTime());
      if (!expiry || now > expiry) continue;

      const previous = activeMatches.get(matchedColor);
      if (!previous || expiry > previous.expiry) {
        activeMatches.set(matchedColor, { order, product, orderDate, expiry });
      }
    }
  }

  for (const color of colorsPriority) {
    if (activeMatches.has(color)) return color;
  }

  return 'neutral';
};
